// =========================================================================
// Fulfillment module service layer.
//
// Owns the @pc/db surface used by @pc/modules-fulfillment:
//   * Hub-scoped, keyset-paginated queue (`listShipments`).
//   * Per-shipment detail loaders (shipment, packing checks, events,
//     subscribers, public tokens).
//   * Mutations: create-for-order, save packing checks, attach label,
//     mark shipped, add subscriber, mint public tracking token, save
//     settings.
//
// Schema lives at supabase/migrations/20260612010000_fulfillment_init.sql.
// Because the codegen for `Database` types runs at deploy time, this file
// declares its own row interfaces and casts the postgrest-js query
// builders through `any` rather than waiting for generated types to land.
// =========================================================================

import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';
import { unwrapFunctionsInvokeError } from '../internal/functionsInvokeError';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';
import type { Json } from '../generated';

// ---- types ---------------------------------------------------------------

export type FulfillmentShipmentStatus =
  | 'needs_label'
  | 'ready_to_pack'
  | 'packed'
  | 'in_transit'
  | 'out_for_delivery'
  | 'available_for_pickup'
  | 'delayed'
  | 'delivered'
  | 'exception'
  | 'return_to_sender'
  | 'lost'
  | 'investigation'
  | 'cancelled'
  | 'on_hold';

export type FulfillmentShipmentEventType =
  | 'created'
  | 'label_purchased'
  | 'label_refunded'
  | 'acknowledged'
  | 'packed'
  | 'shipped'
  | 'in_transit'
  | 'out_for_delivery'
  | 'available_for_pickup'
  | 'delayed'
  | 'delivered'
  | 'exception'
  | 'return_to_sender'
  | 'lost'
  | 'investigation_opened'
  | 'investigation_closed'
  | 'cancelled'
  | 'held'
  | 'released'
  | 'manual_note';

export type FulfillmentSubscriberChannel = 'email' | 'sms';
export type FulfillmentNotificationDispatchStatus =
  | 'pending'
  | 'sent'
  | 'suppressed'
  | 'failed';
export type FulfillmentPackingFlagMode = 'required' | 'optional' | 'disabled';

export interface FulfillmentShipment {
  id: string;
  org_id: string;
  store_id: string;
  order_id: string;
  status: FulfillmentShipmentStatus;
  carrier: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  label_id: string | null;
  label_cost_cents: number;
  ship_to_snapshot: Json;
  parcel: Json;
  weight_oz: number | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  packed_at: string | null;
  packed_by: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  hold_until: string | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  source_locale: string;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  archived_by: string | null;
}

export interface FulfillmentShipmentSummary {
  id: string;
  org_id: string;
  store_id: string;
  order_id: string;
  status: FulfillmentShipmentStatus;
  carrier: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  label_id: string | null;
  label_cost_cents: number;
  acknowledged_at: string | null;
  packed_at: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  hold_until: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  order_number: string;
  /**
   * Sum of unshipped + un-picked-up qty across this shipment's parent
   * order. Powers the "remaining to ship" indicator in the queue. Added
   * by supabase/migrations/20260627001100_fulfillment_split_shipments_and_line_allocations.sql.
   */
  order_remaining_qty: number;
  /**
   * Board fields (20260703480000): the order's client + store identity and
   * payment state, so the shipment board can render the location, client
   * name, and the Paid milestone dot without N+1 lookups. `client_name`
   * falls back to the order's client_snapshot when the reader can't see
   * (or no longer has) the client row.
   */
  client_id: string;
  client_name: string | null;
  store_name: string | null;
  order_status: string;
  order_paid: boolean;
  /**
   * Operator order-alert rollup (20260704000900): active rows from
   * `orders.sale_alerts` on this shipment's parent order, so the queue /
   * board / station lists can flag alerted orders without N+1 lookups.
   * `max_alert_severity` drives the badge color; `latest_alert_title` is
   * the highest-severity newest active alert for tooltips/compact rows.
   */
  active_alert_count: number;
  max_alert_severity: 'info' | 'warning' | 'critical' | null;
  latest_alert_title: string | null;
}

export interface FulfillmentPackingItemCheck {
  order_line_id?: string;
  sku?: string;
  description?: string;
  qty_expected: number;
  qty_packed: number;
  note?: string;
}

export interface FulfillmentPackingChecks {
  shipment_id: string;
  org_id: string;
  item_checks: FulfillmentPackingItemCheck[];
  photos: string[];
  was_reconstituted: boolean | null;
  included_needles: boolean | null;
  shipped_with_ice_pack: boolean | null;
  signed_by: string | null;
  signed_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface FulfillmentShipmentEvent {
  id: string;
  shipment_id: string;
  org_id: string;
  event_type: FulfillmentShipmentEventType;
  payload: Json;
  notification_status: FulfillmentNotificationDispatchStatus;
  notification_attempts: number;
  last_notification_error: string | null;
  created_at: string;
  created_by: string | null;
}

export interface FulfillmentShipmentSubscriber {
  id: string;
  shipment_id: string;
  org_id: string;
  channel: FulfillmentSubscriberChannel;
  address: string;
  consented_at: string;
  unsubscribed_at: string | null;
  created_at: string;
}

export interface FulfillmentPublicTrackingToken {
  token: string;
  shipment_id: string;
  org_id: string;
  expires_at: string | null;
  created_at: string;
  created_by: string | null;
}

export interface FulfillmentDeliveryFeedbackSummary {
  id: string;
  shipment_id: string;
  order_id: string;
  org_id: string;
  store_id: string;
  rating: number;
  note: string | null;
  followup_answers: Json;
  followup_snapshot: Json;
  submitted_at: string;
  shipment_status: FulfillmentShipmentStatus;
  carrier: string | null;
  tracking_number: string | null;
  delivered_at: string | null;
  order_number: string;
}

export interface FulfillmentShipmentDeliveryFeedback {
  id: string;
  shipment_id: string;
  order_id: string;
  org_id: string;
  store_id: string;
  rating: number;
  note: string | null;
  followup_answers: Json;
  followup_snapshot: Json;
  submitted_at: string;
}

export interface FulfillmentDeliveryFeedbackFormQuestion {
  id: string;
  label: string;
  active: boolean;
}

export interface FulfillmentDeliveryFeedbackForm {
  org_id: string;
  title: string;
  intro_text: string;
  note_label: string;
  note_placeholder: string;
  low_rating_threshold: number;
  low_rating_prompt: string;
  low_rating_questions: FulfillmentDeliveryFeedbackFormQuestion[];
  updated_at: string;
  updated_by: string | null;
}

export interface FulfillmentSettings {
  org_id: string;
  was_reconstituted_mode: FulfillmentPackingFlagMode;
  included_needles_mode: FulfillmentPackingFlagMode;
  shipped_with_ice_pack_mode: FulfillmentPackingFlagMode;
  require_photos: boolean;
  min_photos: number;
  require_signature: boolean;
  auto_acknowledge_on_label: boolean;
  auto_pack_on_label: boolean;
  sms_enabled: boolean;
  email_enabled: boolean;
  tracking_brand: Json;
  extras: Json;
  updated_at: string;
  updated_by: string | null;
}

export interface ListShipmentsInput extends KeysetInput {
  status?: FulfillmentShipmentStatus | FulfillmentShipmentStatus[];
  search?: string;
  /**
   * When true, only return shipments whose parent order still has
   * unshipped + un-picked-up qty (i.e. `order_remaining_qty > 0`).
   * Powers the queue's "orders with unshipped items" filter.
   */
  onlyWithRemainder?: boolean;
  /**
   * When true, drop the store_id pin and return shipments across every
   * store in the org the reader can access (RLS still applies per row).
   * Powers the org-wide shipment board; backed by
   * `fulfillment_shipments_org_status_keyset_idx`.
   */
  allStores?: boolean;
}

// =========================================================================
// Split-shipment + line-allocation types
//
// Schema lives at supabase/migrations/20260627001100_fulfillment_split_shipments_and_line_allocations.sql.
// The generated `Database` type lags new fulfillment.* tables (codegen runs
// at deploy time); we declare interfaces here and route writes through the
// new RPCs.
// =========================================================================

export interface FulfillmentShipmentLine {
  order_line_id: string;
  quantity: number;
}

export interface FulfillmentOrderFulfillmentLine {
  order_line_id: string;
  position: number;
  description: string;
  quantity: number;
  shipped_qty: number;
  picked_up_qty: number;
  remaining_qty: number;
}

export interface FulfillmentOrderFulfillmentShipment {
  id: string;
  status: FulfillmentShipmentStatus;
  carrier: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  label_id: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  lines: FulfillmentShipmentLine[];
}

export interface FulfillmentOrderFulfillmentPickup {
  id: string;
  order_line_id: string;
  quantity: number;
  picked_up_at: string;
  picked_up_by: string | null;
  note: string | null;
}

export interface FulfillmentOrderFulfillmentTotals {
  ordered_qty: number;
  shipped_qty: number;
  picked_up_qty: number;
  remaining_qty: number;
  has_remaining: boolean;
}

export interface FulfillmentOrderFulfillmentState {
  order: {
    id: string;
    number: string;
    order_type: 'pickup' | 'ship' | 'digital';
    status: string;
    shipping_address_snapshot: Json | null;
  };
  lines: FulfillmentOrderFulfillmentLine[];
  shipments: FulfillmentOrderFulfillmentShipment[];
  pickups: FulfillmentOrderFulfillmentPickup[];
  totals: FulfillmentOrderFulfillmentTotals;
}

export interface ListDeliveryFeedbackInput extends KeysetInput {
  rating?: number | number[];
  search?: string;
}

// Schema generic helper: the generated `Database` type does not yet
// include `fulfillment.*`, so we narrow to `any` at the .schema() call
// site rather than sprinkling casts at every property access.
function fulfillmentSchema() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (supabase() as any).schema('fulfillment');
}

function coreSchema() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (supabase() as any).schema('core');
}

// =========================================================================
// Hub queue (paginated)
// =========================================================================

/**
 * Keyset-paginated shipment queue for the active hub (store).
 *
 * The `shipping.label-panel` slot in @pc/modules-shipping is replaced by
 * this module's `FulfillmentShipmentPanel`, so the queue is the single
 * "what's on my bench right now" surface. Indexes hit on the hot path:
 *
 *   * `fulfillment_shipments_hub_keyset_idx` for the unfiltered list.
 *   * `fulfillment_shipments_status_keyset_idx` when the operator filters
 *     by status (the default queue shows Needs label + Ready to pack).
 */
export async function listShipments(
  ctx: ActiveContext,
  input: ListShipmentsInput = {},
): Promise<ListResult<FulfillmentShipmentSummary>> {
  const { orgId, storeId } = requireContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = fulfillmentSchema()
    .from('v_shipment_summary')
    .select('*', selectCountOption(input.countMode))
    .eq('org_id', orgId);

  if (!input.allStores) {
    q = q.eq('store_id', storeId);
  }

  if (Array.isArray(input.status) && input.status.length > 0) {
    q = q.in('status', input.status);
  } else if (typeof input.status === 'string') {
    q = q.eq('status', input.status);
  }
  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or(
      `order_number.ilike.${s},tracking_number.ilike.${s},client_name.ilike.${s}`,
    );
  }
  if (input.onlyWithRemainder) {
    q = q.gt('order_remaining_qty', 0);
  }

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as FulfillmentShipmentSummary[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

/**
 * Bounded one-shot list. Powers the dashboard widget + sidebar "needs
 * attention" counters.
 */
export async function listShipmentsTop(
  ctx: ActiveContext,
  input: Omit<ListShipmentsInput, 'cursor' | 'pageSize' | 'countMode'> & {
    limit?: number;
  } = {},
): Promise<FulfillmentShipmentSummary[]> {
  const { orgId, storeId } = requireContext(ctx);
  const limit = Math.min(input.limit ?? 10, 50);
  let q = fulfillmentSchema()
    .from('v_shipment_summary')
    .select('*')
    .eq('org_id', orgId)
    .eq('store_id', storeId);
  if (Array.isArray(input.status) && input.status.length > 0) {
    q = q.in('status', input.status);
  } else if (typeof input.status === 'string') {
    q = q.eq('status', input.status);
  }
  q = q.order('updated_at', { ascending: false }).order('id', { ascending: false }).limit(limit);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as FulfillmentShipmentSummary[];
}

// =========================================================================
// Per-shipment detail
// =========================================================================

export async function getShipment(
  ctx: ActiveContext,
  id: string,
): Promise<FulfillmentShipment | null> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema()
    .from('shipments')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as FulfillmentShipment | null;
}

export async function getShipmentForOrder(
  ctx: ActiveContext,
  orderId: string,
): Promise<FulfillmentShipment | null> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema()
    .from('shipments')
    .select('*')
    .eq('order_id', orderId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as FulfillmentShipment | null;
}

export async function getPackingChecks(
  ctx: ActiveContext,
  shipmentId: string,
): Promise<FulfillmentPackingChecks | null> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema()
    .from('shipment_packing_checks')
    .select('*')
    .eq('shipment_id', shipmentId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as FulfillmentPackingChecks | null;
}

export async function listShipmentEvents(
  ctx: ActiveContext,
  shipmentId: string,
  limit = 100,
): Promise<FulfillmentShipmentEvent[]> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema()
    .from('shipment_events')
    .select('*')
    .eq('shipment_id', shipmentId)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(Math.min(limit, 500));
  if (error) throw error;
  return (data ?? []) as FulfillmentShipmentEvent[];
}

export async function listShipmentSubscribers(
  ctx: ActiveContext,
  shipmentId: string,
): Promise<FulfillmentShipmentSubscriber[]> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema()
    .from('shipment_subscribers')
    .select('*')
    .eq('shipment_id', shipmentId)
    .is('unsubscribed_at', null)
    .order('created_at', { ascending: true })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as FulfillmentShipmentSubscriber[];
}

export async function listShipmentTokens(
  ctx: ActiveContext,
  shipmentId: string,
): Promise<FulfillmentPublicTrackingToken[]> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema()
    .from('public_tracking_tokens')
    .select('*')
    .eq('shipment_id', shipmentId)
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) throw error;
  return (data ?? []) as FulfillmentPublicTrackingToken[];
}

export async function listDeliveryFeedback(
  ctx: ActiveContext,
  input: ListDeliveryFeedbackInput = {},
): Promise<ListResult<FulfillmentDeliveryFeedbackSummary>> {
  const { orgId, storeId } = requireContext(ctx);
  const pageSize = clampPageSize(input.pageSize);
  let q = fulfillmentSchema()
    .from('v_delivery_feedback_summary')
    .select('*', selectCountOption(input.countMode))
    .eq('org_id', orgId)
    .eq('store_id', storeId);

  if (Array.isArray(input.rating) && input.rating.length > 0) {
    q = q.in('rating', input.rating);
  } else if (typeof input.rating === 'number') {
    q = q.eq('rating', input.rating);
  }
  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or(`order_number.ilike.${s},note.ilike.${s},tracking_number.ilike.${s}`);
  }

  q = applyKeyset(q, input.cursor, 'submitted_at');
  q = q.limit(pageSize);
  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as FulfillmentDeliveryFeedbackSummary[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'submitted_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function getDeliveryFeedbackForShipment(
  ctx: ActiveContext,
  shipmentId: string,
): Promise<FulfillmentShipmentDeliveryFeedback | null> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema()
    .from('shipment_delivery_feedback')
    .select('*')
    .eq('shipment_id', shipmentId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as FulfillmentShipmentDeliveryFeedback | null;
}

const DEFAULT_FEEDBACK_QUESTIONS: FulfillmentDeliveryFeedbackFormQuestion[] = [
  { id: 'arrived_on_time', label: 'Did your order arrive on time?', active: true },
  { id: 'packaging_intact', label: 'Was the packaging intact?', active: true },
  { id: 'would_reorder', label: 'Would you order again based on this delivery?', active: true },
];

export async function getDeliveryFeedbackForm(
  ctx: ActiveContext,
): Promise<FulfillmentDeliveryFeedbackForm> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await fulfillmentSchema()
    .from('delivery_feedback_forms')
    .select('*')
    .eq('org_id', orgId)
    .maybeSingle();
  if (error) throw error;
  if (data) {
    const row = data as FulfillmentDeliveryFeedbackForm & { low_rating_questions?: unknown };
    return {
      ...row,
      low_rating_questions: Array.isArray(row.low_rating_questions)
        ? (row.low_rating_questions as FulfillmentDeliveryFeedbackFormQuestion[])
        : DEFAULT_FEEDBACK_QUESTIONS,
    };
  }
  return {
    org_id: orgId,
    title: 'How did your order arrive?',
    intro_text: 'Quick 30-second check-in. Your rating is saved to this order.',
    note_label: 'Optional note',
    note_placeholder: 'Anything we should know about delivery condition?',
    low_rating_threshold: 3,
    low_rating_prompt: 'Help us improve by answering a couple of quick yes/no questions.',
    low_rating_questions: DEFAULT_FEEDBACK_QUESTIONS,
    updated_at: new Date().toISOString(),
    updated_by: null,
  };
}

export type UpsertDeliveryFeedbackFormInput = Pick<
  FulfillmentDeliveryFeedbackForm,
  | 'title'
  | 'intro_text'
  | 'note_label'
  | 'note_placeholder'
  | 'low_rating_threshold'
  | 'low_rating_prompt'
  | 'low_rating_questions'
>;

export async function upsertDeliveryFeedbackForm(
  ctx: ActiveContext,
  input: UpsertDeliveryFeedbackFormInput,
): Promise<FulfillmentDeliveryFeedbackForm> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await fulfillmentSchema()
    .from('delivery_feedback_forms')
    .upsert(
      {
        org_id: orgId,
        title: input.title.trim(),
        intro_text: input.intro_text.trim(),
        note_label: input.note_label.trim(),
        note_placeholder: input.note_placeholder.trim(),
        low_rating_threshold: input.low_rating_threshold,
        low_rating_prompt: input.low_rating_prompt.trim(),
        low_rating_questions: input.low_rating_questions as unknown as Json,
      },
      { onConflict: 'org_id' },
    )
    .select('*')
    .single();
  if (error) throw error;
  const row = data as FulfillmentDeliveryFeedbackForm & { low_rating_questions?: unknown };
  return {
    ...row,
    low_rating_questions: Array.isArray(row.low_rating_questions)
      ? (row.low_rating_questions as FulfillmentDeliveryFeedbackFormQuestion[])
      : DEFAULT_FEEDBACK_QUESTIONS,
  };
}

// =========================================================================
// Mutations (RPCs)
// =========================================================================

export async function createShipmentForOrder(
  ctx: ActiveContext,
  orderId: string,
): Promise<string> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema().rpc(
    'rpc_create_shipment_for_order',
    { p_order_id: orderId },
  );
  if (error) throw error;
  return data as string;
}

export interface SavePackingChecksInput {
  shipmentId: string;
  itemChecks: FulfillmentPackingItemCheck[];
  photos: string[];
  wasReconstituted: boolean | null;
  includedNeedles: boolean | null;
  shippedWithIcePack: boolean | null;
  signedBy: string | null;
  advanceStatus?: boolean;
  /**
   * The active station operator (PIN-selected on a shared fulfillment tablet)
   * credited as the packer. Server-validated against org membership; falls
   * back to the session user when null. Ships in migration
   * 20260703040000_core_user_screen_lock_pins.sql; cast loosely below until
   * the generated `Database` types pick up the new parameter.
   */
  actingUserId?: string | null;
}

export async function savePackingChecks(
  ctx: ActiveContext,
  input: SavePackingChecksInput,
): Promise<void> {
  requireContext(ctx);
  const rpc = fulfillmentSchema() as unknown as {
    rpc: (
      name: string,
      args: Record<string, unknown>,
    ) => Promise<{ error: unknown }>;
  };
  const { error } = await rpc.rpc('rpc_save_packing_checks', {
    p_shipment_id: input.shipmentId,
    p_item_checks: input.itemChecks as unknown as Json,
    p_photos: input.photos,
    p_was_reconstituted: input.wasReconstituted,
    p_included_needles: input.includedNeedles,
    p_shipped_with_ice_pack: input.shippedWithIcePack,
    p_signed_by: input.signedBy,
    p_advance_status: input.advanceStatus ?? true,
    p_acting_user_id: input.actingUserId ?? null,
  });
  if (error) throw error;
}

export async function attachLabelToShipment(
  ctx: ActiveContext,
  input: { shipmentId: string; labelId: string },
): Promise<void> {
  requireContext(ctx);
  const { error } = await fulfillmentSchema().rpc('rpc_attach_label', {
    p_shipment_id: input.shipmentId,
    p_label_id: input.labelId,
  });
  if (error) throw error;
}

export async function markShipmentShipped(
  ctx: ActiveContext,
  shipmentId: string,
): Promise<void> {
  requireContext(ctx);
  const { error } = await fulfillmentSchema().rpc('rpc_mark_shipped', {
    p_shipment_id: shipmentId,
  });
  if (error) throw error;
}

export async function addShipmentSubscriber(
  ctx: ActiveContext,
  input: {
    shipmentId: string;
    channel: FulfillmentSubscriberChannel;
    address: string;
  },
): Promise<string> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema().rpc('rpc_add_subscriber', {
    p_shipment_id: input.shipmentId,
    p_channel: input.channel,
    p_address: input.address.trim(),
  });
  if (error) throw error;
  return data as string;
}

export async function unsubscribeShipmentSubscriber(
  ctx: ActiveContext,
  subscriberId: string,
): Promise<void> {
  requireContext(ctx);
  const { error } = await fulfillmentSchema()
    .from('shipment_subscribers')
    .update({ unsubscribed_at: new Date().toISOString() })
    .eq('id', subscriberId);
  if (error) throw error;
}

export interface ReconcileShipmentTrackingResult {
  ok: boolean;
  mode?: string;
  /** True when the carrier returned a status that changed the shipment. */
  updated: boolean;
  /** Mapped status forwarded to rpc_apply_webhook_status (e.g. 'delivered'). */
  status?: string;
  /** Raw carrier status string (e.g. 'DELIVERED'). */
  carrier_status?: string;
  /** Machine reason when `updated` is false (no_carrier_account, carrier_no_status, ...). */
  reason?: string;
  /** Operator-facing explanation. */
  message?: string;
}

/**
 * Operator-triggered "Refresh from carrier" for a single shipment.
 *
 * Invokes the `fulfillment-track-reconcile` Edge Function in `mode=manual`.
 * The function authorizes the caller against org access (via
 * `fulfillment.rpc_track_reconcile_candidate_one` under the user JWT), then
 * polls Shippo with the resolved carrier token and applies any status change
 * through `rpc_apply_webhook_status` (which advances the shipment and queues
 * customer notifications). Unlike the cron, it works for shipments in ANY
 * status and falls back to any active org-level Shippo account when the
 * shipment's own store has none.
 *
 * Returns a structured outcome instead of throwing on the "nothing to do"
 * cases (no carrier connection, carrier has no status yet) so the UI can
 * explain what happened; only transport / authorization failures throw.
 */
export async function reconcileShipmentTracking(
  ctx: ActiveContext,
  shipmentId: string,
): Promise<ReconcileShipmentTrackingResult> {
  requireContext(ctx);
  const { data, error } = await supabase().functions.invoke<ReconcileShipmentTrackingResult>(
    'fulfillment-track-reconcile?mode=manual',
    { body: { mode: 'manual', shipment_id: shipmentId } },
  );
  if (error) {
    throw new Error(
      await unwrapFunctionsInvokeError(error, 'Could not refresh tracking from the carrier.'),
    );
  }
  return (data ?? { ok: false, updated: false }) as ReconcileShipmentTrackingResult;
}

export async function mintPublicTrackingToken(
  ctx: ActiveContext,
  shipmentId: string,
): Promise<string> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema().rpc(
    'rpc_mint_public_tracking_token',
    { p_shipment_id: shipmentId },
  );
  if (error) throw error;
  return data as string;
}

// =========================================================================
// Split-shipment + line-allocation operations
//
// Wraps the RPCs introduced in
// supabase/migrations/20260627001100_fulfillment_split_shipments_and_line_allocations.sql.
// Every mutation routes through a SECURITY DEFINER RPC so the
// fn_validate_line_allocation trigger guards "shipped + picked-up <=
// ordered" without the caller having to re-implement the math.
// =========================================================================

export interface ShipmentLineInput {
  orderLineId: string;
  quantity: number;
}

/**
 * List every shipment attached to an order. Includes cancelled rows so
 * the order detail page can show the full audit trail; UI should filter
 * `cancelled_at` for the "active shipments" view.
 */
export async function listShipmentsForOrder(
  ctx: ActiveContext,
  orderId: string,
): Promise<FulfillmentShipment[]> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema()
    .from('shipments')
    .select('*')
    .eq('order_id', orderId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as FulfillmentShipment[];
}

export interface ShipmentLineWithDescription {
  order_line_id: string;
  quantity: number;
  description: string;
  position: number;
  shipped_qty: number;
  picked_up_qty: number;
}

/**
 * Lines assigned to a specific shipment, joined with the order_line
 * description + per-line allocation totals so the packing checklist
 * doesn't have to do its own join. Cheap: shipment_lines is keyset-
 * indexed by shipment_id and orders.order_lines has the corresponding
 * primary key lookup.
 */
export async function getShipmentLinesWithDescriptions(
  ctx: ActiveContext,
  shipmentId: string,
): Promise<ShipmentLineWithDescription[]> {
  requireContext(ctx);
  // Look up the order_id first so we can call rpc_get_order_fulfillment_state
  // and reuse its per-line shipped/picked-up totals. Single RPC,
  // RLS-checked. Falls back to an empty list if the shipment has no
  // line assignments (legacy shipment).
  const ship = await getShipment(ctx, shipmentId);
  if (!ship) return [];
  const state = await getOrderFulfillmentState(ctx, ship.order_id);
  const target = state.shipments.find((s) => s.id === shipmentId);
  if (!target) return [];
  return target.lines.map((sl) => {
    const line = state.lines.find((l) => l.order_line_id === sl.order_line_id);
    return {
      order_line_id: sl.order_line_id,
      quantity: Number(sl.quantity),
      description: line?.description ?? 'Order line',
      position: line?.position ?? 0,
      shipped_qty: Number(line?.shipped_qty ?? 0),
      picked_up_qty: Number(line?.picked_up_qty ?? 0),
    };
  });
}

/**
 * Per-order allocation snapshot. The operator UI uses this to render the
 * "remaining to ship" banner, the per-line allocation badges, and the
 * "this shipment contains" list. Single round-trip; the RPC stitches
 * order_lines + shipments + shipment_lines + order_line_pickups.
 */
export async function getOrderFulfillmentState(
  ctx: ActiveContext,
  orderId: string,
): Promise<FulfillmentOrderFulfillmentState> {
  requireContext(ctx);
  const { data, error } = await fulfillmentSchema().rpc(
    'rpc_get_order_fulfillment_state',
    { p_order_id: orderId },
  );
  if (error) throw error;
  return data as unknown as FulfillmentOrderFulfillmentState;
}

/**
 * Create a new shipment seeded with explicit line allocations. If
 * `lines` is empty/omitted the shipment starts empty and lines can be
 * added later via setShipmentLines.
 */
export async function createShipmentWithLines(
  ctx: ActiveContext,
  input: { orderId: string; lines: ShipmentLineInput[] },
): Promise<string> {
  requireContext(ctx);
  const payload = (input.lines ?? [])
    .filter((l) => l.quantity > 0)
    .map((l) => ({ order_line_id: l.orderLineId, quantity: l.quantity }));
  const { data, error } = await fulfillmentSchema().rpc(
    'rpc_create_shipment_with_lines',
    { p_order_id: input.orderId, p_lines: payload as unknown as Json },
  );
  if (error) throw error;
  return data as string;
}

/**
 * Replace the line allocations on an existing (unshipped) shipment.
 * Atomic swap inside the RPC; the validation trigger will reject any
 * overflow before the transaction commits.
 */
export async function setShipmentLines(
  ctx: ActiveContext,
  input: { shipmentId: string; lines: ShipmentLineInput[] },
): Promise<void> {
  requireContext(ctx);
  const payload = (input.lines ?? [])
    .filter((l) => l.quantity > 0)
    .map((l) => ({ order_line_id: l.orderLineId, quantity: l.quantity }));
  const { error } = await fulfillmentSchema().rpc('rpc_set_shipment_lines', {
    p_shipment_id: input.shipmentId,
    p_lines: payload as unknown as Json,
  });
  if (error) throw error;
}

/**
 * Mark a set of order lines as picked-up (handed to the customer at the
 * counter, etc.). If this leaves the order fully allocated the RPC
 * auto-flips the order to status='fulfilled'.
 */
export async function markOrderLinesPickedUp(
  ctx: ActiveContext,
  input: {
    orderId: string;
    lines: Array<{ orderLineId: string; quantity: number; note?: string }>;
    pickedUpBy?: string | null;
  },
): Promise<void> {
  requireContext(ctx);
  const payload = (input.lines ?? [])
    .filter((l) => l.quantity > 0)
    .map((l) => ({
      order_line_id: l.orderLineId,
      quantity: l.quantity,
      note: l.note ?? null,
    }));
  const { error } = await fulfillmentSchema().rpc(
    'rpc_mark_order_lines_picked_up',
    {
      p_order_id: input.orderId,
      p_lines: payload as unknown as Json,
      p_picked_up_by: input.pickedUpBy ?? null,
    },
  );
  if (error) throw error;
}

/**
 * Cancel an open shipment. Frees the line allocations so a fresh
 * shipment can claim them; refuses to cancel a shipment that has
 * already shipped (the operator must refund the label instead).
 */
export async function cancelShipment(
  ctx: ActiveContext,
  input: { shipmentId: string; reason?: string | null },
): Promise<void> {
  requireContext(ctx);
  const { error } = await fulfillmentSchema().rpc('rpc_cancel_shipment', {
    p_shipment_id: input.shipmentId,
    p_reason: input.reason ?? null,
  });
  if (error) throw error;
}

export async function recordManualShipmentTracking(
  ctx: ActiveContext,
  input: {
    shipmentId: string;
    carrier: string;
    trackingNumber: string;
    trackingUrl?: string | null;
    markShipped?: boolean;
  },
): Promise<void> {
  requireContext(ctx);
  const { error } = await fulfillmentSchema().rpc('rpc_record_manual_tracking', {
    p_shipment_id: input.shipmentId,
    p_carrier: input.carrier,
    p_tracking_number: input.trackingNumber,
    p_tracking_url: input.trackingUrl ?? null,
    p_mark_shipped: input.markShipped ?? true,
  });
  if (error) throw error;
}

// =========================================================================
// Settings (per-org singleton)
// =========================================================================

const DEFAULT_SETTINGS: Omit<FulfillmentSettings, 'org_id' | 'updated_at' | 'updated_by'> = {
  was_reconstituted_mode: 'optional',
  included_needles_mode: 'optional',
  shipped_with_ice_pack_mode: 'optional',
  require_photos: false,
  min_photos: 0,
  require_signature: false,
  auto_acknowledge_on_label: true,
  auto_pack_on_label: false,
  sms_enabled: true,
  email_enabled: true,
  tracking_brand: {} as Json,
  extras: {} as Json,
};

export async function getFulfillmentSettings(
  ctx: ActiveContext,
): Promise<FulfillmentSettings> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await fulfillmentSchema()
    .from('settings')
    .select('*')
    .eq('org_id', orgId)
    .maybeSingle();
  if (error) throw error;
  if (data) return data as FulfillmentSettings;
  // Synthesize a virtual default row so the Settings page can render
  // before the first save. The first upsert materializes the row.
  return {
    org_id: orgId,
    ...DEFAULT_SETTINGS,
    updated_at: new Date().toISOString(),
    updated_by: null,
  };
}

export type UpsertFulfillmentSettingsInput = Partial<
  Omit<FulfillmentSettings, 'org_id' | 'updated_at' | 'updated_by'>
>;

export async function upsertFulfillmentSettings(
  ctx: ActiveContext,
  patch: UpsertFulfillmentSettingsInput,
): Promise<FulfillmentSettings> {
  const { orgId } = requireContext(ctx);
  const existing = await getFulfillmentSettings(ctx);
  const merged = {
    org_id: orgId,
    was_reconstituted_mode: patch.was_reconstituted_mode ?? existing.was_reconstituted_mode,
    included_needles_mode: patch.included_needles_mode ?? existing.included_needles_mode,
    shipped_with_ice_pack_mode:
      patch.shipped_with_ice_pack_mode ?? existing.shipped_with_ice_pack_mode,
    require_photos: patch.require_photos ?? existing.require_photos,
    min_photos: patch.min_photos ?? existing.min_photos,
    require_signature: patch.require_signature ?? existing.require_signature,
    auto_acknowledge_on_label:
      patch.auto_acknowledge_on_label ?? existing.auto_acknowledge_on_label,
    auto_pack_on_label: patch.auto_pack_on_label ?? existing.auto_pack_on_label,
    sms_enabled: patch.sms_enabled ?? existing.sms_enabled,
    email_enabled: patch.email_enabled ?? existing.email_enabled,
    tracking_brand: patch.tracking_brand ?? existing.tracking_brand,
    extras: patch.extras ?? existing.extras,
  };
  const { data, error } = await fulfillmentSchema()
    .from('settings')
    .upsert(merged, { onConflict: 'org_id' })
    .select('*')
    .single();
  if (error) throw error;
  return data as FulfillmentSettings;
}

// =========================================================================
// Hub flag (writes to core.stores; read by every fulfillment page)
// =========================================================================

export interface ShipmentHubStore {
  id: string;
  name: string;
  is_shipment_hub: boolean;
  archived_at: string | null;
}

export async function listShipmentHubs(
  ctx: ActiveContext,
): Promise<ShipmentHubStore[]> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await coreSchema()
    .from('stores')
    .select('id, name, is_shipment_hub, archived_at')
    .eq('org_id', orgId)
    .is('archived_at', null)
    .order('name', { ascending: true })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as ShipmentHubStore[];
}

export async function setStoreIsShipmentHub(
  ctx: ActiveContext,
  storeId: string,
  isHub: boolean,
): Promise<void> {
  requireContext(ctx);
  // core.stores has RLS enabled + forced but ships no UPDATE policy, so a
  // direct `.update()` here silently no-ops (0 rows affected, no error)
  // and the refetch puts the checkbox back to its prior value. Route
  // through the SECURITY DEFINER admin RPC instead -- see
  // supabase/migrations/20260612010600_core_rpc_set_store_is_shipment_hub.sql.
  const { error } = await coreSchema().rpc('rpc_set_store_is_shipment_hub', {
    p_store_id: storeId,
    p_is_hub: isHub,
  });
  if (error) throw error;
}

// =========================================================================
// Photo upload helper
// =========================================================================

/**
 * Uploads a packing photo into the `fulfillment-photos` storage bucket
 * under `<org_id>/<shipment_id>/<random>.<ext>` and returns the storage
 * path. The bucket is private; the rendering UI swaps the path for a
 * short-lived signed URL via `getSignedPhotoUrls` below.
 */
export async function uploadPackingPhoto(
  ctx: ActiveContext,
  shipmentId: string,
  file: File,
): Promise<string> {
  const { orgId } = requireContext(ctx);
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
  const safeExt = ext.length > 0 && ext.length <= 5 ? ext : 'jpg';
  const path = `${orgId}/${shipmentId}/${crypto.randomUUID()}.${safeExt}`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase() as any).storage
    .from('fulfillment-photos')
    .upload(path, file, { contentType: file.type, cacheControl: '3600', upsert: false });
  if (error) throw error;
  return path;
}

export async function getSignedPhotoUrls(
  paths: string[],
  expiresIn = 3600,
): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase() as any).storage
    .from('fulfillment-photos')
    .createSignedUrls(paths, expiresIn);
  if (error) throw error;
  const map: Record<string, string> = {};
  for (const row of (data ?? []) as Array<{ path: string; signedUrl: string }>) {
    if (row.path && row.signedUrl) map[row.path] = row.signedUrl;
  }
  return map;
}
